import React, { useEffect, useState } from 'react';
import { Modal, Radio, Space, Typography, Tag, type RadioChangeEvent } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text } = Typography;

/**
 * Mirror of `pending_receipts_for(ro)` in
 * `app/controllers/api/operator/v1/return_receipts_controller.rb`.
 *
 * If you change the BE shape, update this interface AND the i18n keys
 * referenced in render below.
 */
export interface ActivePendingReceipt {
  id: string;
  name: string;
  /**
   * Receipt status. BE emits `:pending` (init_draft created the row but
   * nothing scanned) or `:in_progress` (operator already saved scanned
   * items via save_progress). FE uses this to differentiate the row label.
   */
  status?: 'pending' | 'in_progress';
  tracking_number?: string | null;
  videos_count: number;
  item_progress: { scanned: number; expected: number };
  created_at?: string;
  updated_at?: string;
}

/**
 * `null` = operator chose "新開"; otherwise the existing pending receipt id
 * to resume.
 */
export type ReceiptSelection = string | null;

export interface ReturnReceiptSelectorModalProps {
  open: boolean;
  activePendingReceipts?: ActivePendingReceipt[] | null;
  onConfirm: (receiptId: ReceiptSelection) => void;
  onCancel: () => void;
}

const NEW_RECEIPT_VALUE = 'NEW' as const;
type RadioValue = string | typeof NEW_RECEIPT_VALUE;

/**
 * Receipt selector for ReturnOrderVerification.
 *
 * Shown when a search hits a ReturnOrder that already has 1+ pending
 * ReturnReceipts. Operator picks which to resume, or chooses to start a new one.
 */
const ReturnReceiptSelectorModal: React.FC<ReturnReceiptSelectorModalProps> = ({
  open,
  activePendingReceipts,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<RadioValue>(NEW_RECEIPT_VALUE);

  useEffect(() => {
    if (open) setSelected(NEW_RECEIPT_VALUE);
  }, [open]);

  const handleOk = (): void => {
    onConfirm(selected === NEW_RECEIPT_VALUE ? null : selected);
  };

  const receipts: ActivePendingReceipt[] = activePendingReceipts ?? [];

  return (
    <Modal
      open={open}
      title={t('operator.return_verification.selector.title', { defaultValue: '選擇驗收單' })}
      onCancel={onCancel}
      onOk={handleOk}
      okText={t('common.button.confirm', { defaultValue: '確認' })}
      cancelText={t('common.button.cancel', { defaultValue: '取消' })}
      destroyOnClose
    >
      <Text type="secondary">
        {t('operator.return_verification.selector.description', {
          defaultValue: '此退貨單已有進行中的驗收單，請選擇要接續的單據或新開一張：',
        })}
      </Text>
      <Radio.Group
        value={selected}
        onChange={(e: RadioChangeEvent) => setSelected(e.target.value as RadioValue)}
        style={{ display: 'block', marginTop: 16 }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {receipts.map((rr) => {
            const isInProgress = rr.status === 'in_progress';
            const statusLabel = isInProgress
              ? t('operator.return_verification.selector.status_in_progress', {
                  defaultValue: '進行中',
                })
              : t('operator.return_verification.selector.status_pending', {
                  defaultValue: '尚未開始',
                });
            return (
              <Radio key={rr.id} value={rr.id} style={{ display: 'block' }}>
                <Tag color={isInProgress ? 'processing' : 'default'}>{statusLabel}</Tag>
                <Text strong>{rr.name}</Text>
                <div style={{ marginLeft: 24, color: '#666', fontSize: 12 }}>
                  {rr.tracking_number
                    ? `${t('operator.return_verification.selector.tracking', { defaultValue: '追蹤號' })}: ${rr.tracking_number} · `
                    : ''}
                  {t('operator.return_verification.selector.scanned', {
                    defaultValue: '{{scanned}} / {{total}} 已掃',
                    scanned: rr.item_progress?.scanned ?? 0,
                    total: rr.item_progress?.expected ?? 0,
                  })}
                  {rr.videos_count > 0
                    ? ` · ${t('operator.return_verification.selector.videos', {
                        defaultValue: '{{count}} 支影片',
                        count: rr.videos_count,
                      })}`
                    : ''}
                  {rr.updated_at ? ` · ${dayjs(rr.updated_at).fromNow()}` : ''}
                </div>
              </Radio>
            );
          })}
          <Radio value={NEW_RECEIPT_VALUE} style={{ display: 'block', marginTop: 8 }}>
            <Tag color="blue">
              {t('operator.return_verification.selector.new', { defaultValue: '新開' })}
            </Tag>
            <Text>
              {t('operator.return_verification.selector.new_description', {
                defaultValue: '建立一張新的驗收單',
              })}
            </Text>
          </Radio>
        </Space>
      </Radio.Group>
    </Modal>
  );
};

export default ReturnReceiptSelectorModal;
